import { func, number, string } from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import SignatureEngine from './signatureEngine';

/**
 * Freehand signature pad built on the custom velocity-smoothed engine (OMM-616).
 *
 * Exports are trimmed to the ink bounding box: signature widgets render the PNG with
 * contain-fit, so cropping the whitespace is what makes the signature fill the field.
 */
const SignatureDrawPad = forwardRef(
  ({ penColor, height = 300, className = '', onChange = null }, ref) => {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const engineRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const penColorRef = useRef(penColor);

    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
      penColorRef.current = penColor;
    }, [penColor]);

    useImperativeHandle(ref, () => ({
      clear: () => engineRef.current?.clear(),
      isEmpty: () => engineRef.current?.isEmpty() ?? true,
      toDataURL: (type = 'image/png') =>
        engineRef.current?.toTrimmedDataURL(type) ?? engineRef.current?.toDataURL(type),
    }));

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return undefined;
      }

      const engine = new SignatureEngine(canvas, {
        penColor: penColorRef.current,
        onEnd: () => {
          if (onChangeRef.current && !engine.isEmpty()) {
            onChangeRef.current(engine.toTrimmedDataURL('image/png'));
          }
        },
      });
      engineRef.current = engine;

      return () => {
        engine.destroy();
        engineRef.current = null;
      };
    }, []);

    useEffect(() => {
      engineRef.current?.setOptions({ penColor });
    }, [penColor]);

    return (
      <div ref={containerRef} style={{ width: '100%', height }}>
        <canvas
          ref={canvasRef}
          className={className}
          style={{ width: '100%', height: '100%', display: 'block', touchAction: 'none' }}
        />
      </div>
    );
  },
);

SignatureDrawPad.displayName = 'SignatureDrawPad';

SignatureDrawPad.propTypes = {
  penColor: string.isRequired,
  height: number,
  className: string,
  onChange: func,
};

export default SignatureDrawPad;
